<template>
	<div class="mt-2">
		<div v-if="colorVariants.length > 0" class="mb-4">
			<div class="flex flex-wrap items-center gap-2">
				<NuxtLink
					v-for="variant in colorVariants"
					:key="variant.Id"
					:to="getVariantRoute(variant, localePath, allParametrics)"
					class="relative flex items-center justify-center rounded-full p-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-gray-super-dark"
					@click="handleVariantClick(variant)"
				>
					<span
						aria-hidden="true"
						:class="['h-6 w-6 rounded-full border border-gray-darker']"
						:style="{
							backgroundColor: getColorValue(
								variant,
								colorParametricId,
								allParametrics || []
							)
						}"
					/>
					<div
						v-if="!isInStock(variant)"
						class="w-[88%] h-0.5 bg-black absolute -rotate-45"
					></div>
				</NuxtLink>
			</div>
		</div>

		<!-- Other variants - rectangular blocks with labels -->
		<div v-if="otherVariants.length > 0" class="flex flex-wrap gap-2">
			<NuxtLink
				v-for="variant in otherVariants"
				:key="variant.Id"
				:to="getVariantRoute(variant, localePath, allParametrics)"
				class="bg-white text-gray-900 cursor-pointer group relative flex items-center justify-center py-2 px-3 text-xs font-medium uppercase hover:bg-gray focus:outline-none border border-gray-dark"
				:class="[!isInStock(variant) ? 'opacity-50' : '']"
				@click="handleVariantClick(variant)"
			>
				<span class="text-gray-700 z-10">
					{{ getVariantLabel(variant, colorParametricId, allParametrics) }}
				</span>
				<span
					:class="[
						'border-gray-dark',
						'shadow-sm border pointer-events-none absolute -inset-px'
					]"
					aria-hidden="true"
				/>
				<span
					v-if="!isInStock(variant)"
					aria-hidden="true"
					class="pointer-events-none absolute -inset-px border-2 border-gray-dark bg-gray-light"
				>
					<svg
						class="absolute inset-0 h-full w-full stroke-2 text-gray-dark"
						viewBox="0 0 100 100"
						preserveAspectRatio="none"
						stroke="currentColor"
					>
						<line
							x1="0"
							y1="100"
							x2="100"
							y2="0"
							vector-effect="non-scaling-stroke"
						/>
					</svg>
				</span>
			</NuxtLink>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	ParametricListInfo,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { useGtmEvents } from "~/composables/useGtmEvents";
import { storeToRefs } from "pinia";
import {
	findColorParametricId,
	getColorValue,
	getVariantLabel,
	getVariantRoute,
	isColorVariant,
	isInStock,
	getUniqueColorVariants,
	getUniqueOtherVariants,
	getFirstNonColorParametricId
} from "~/utils/product/variantHelpers";

interface Props {
	variants: FilteredProductItem[];
	allParametrics: ParametricListValue[];
}

const { variants, allParametrics } = defineProps<Props>();

const { productClick } = useGtmEvents();
const localePath = useLocalePath();
const productStore = useProductStore();
const { getParametricsInformation } = storeToRefs(productStore);

const parametricsInfo = computed<ParametricListInfo[]>(() => {
	return getParametricsInformation.value || [];
});

const colorParametricId = computed<number | null>(() => {
	return findColorParametricId(parametricsInfo.value);
});

// Color variants - priority, but must be unique and more than one
const colorVariants = computed(() => {
	if (
		!colorParametricId.value ||
		!allParametrics ||
		allParametrics.length === 0
	)
		return [];

	const colorVariantsList = variants.filter((v) =>
		isColorVariant(v, colorParametricId.value)
	);
	const uniqueColors = getUniqueColorVariants(
		colorVariantsList,
		colorParametricId.value
	);

	// Only show if there are multiple unique colors (if only one color, don't show variants)
	if (uniqueColors.length <= 1) {
		return [];
	}

	return uniqueColors;
});

// Other variants (size) - only show if no color variants
const otherVariants = computed(() => {
	// If color variants exist (multiple unique colors), don't show other variants (priority to colors)
	if (colorVariants.value.length > 0) {
		return [];
	}

	// If no color or only one color, show size if available
	if (!variants || variants.length === 0) {
		return [];
	}

	// Find first non-color parametric ID from first variant
	const firstVariant = variants[0];
	if (!firstVariant) {
		return [];
	}

	const nonColorParamId = getFirstNonColorParametricId(
		firstVariant,
		colorParametricId.value
	);
	if (!nonColorParamId) {
		return [];
	}

	const otherVariantsList = variants.filter(
		(v) => !isColorVariant(v, colorParametricId.value)
	);
	return getUniqueOtherVariants(otherVariantsList, colorParametricId.value);
});

const handleVariantClick = (variant: FilteredProductItem) => {
	productClick(variant);
};
</script>
